import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  ArrowLeft,
  Save,
  X,
  ImagePlus,
  Package,
  DollarSign,
} from 'lucide-react';
import {
  Button,
  Input,
  Label,
  Textarea,
  Card,
  Select,
  Checkbox,
  Alert,
  apiClient,
  useAuthStore,
  useNotificationStore,
} from '@dukene/shared';
import { ProductStatus, MERCHANT_ROUTES } from '@dukene/shared';
import VariantManager, { Attribute, Variant } from '../../components/products/VariantManager';

interface ProductFormData {
  title: string;
  description: string;
  shortDescription?: string;
  price: number;
  compareAtPrice?: number;
  costPerItem?: number;
  sku: string;
  categoryId?: string;
  status: ProductStatus;
  featured: boolean;
  tags: string;
  trackInventory: boolean;
  initialQuantity?: number;
  lowStockThreshold?: number;
  metaTitle?: string;
  metaDescription?: string;
  metaKeywords?: string;
  // Affiliate marketing
  affiliateEnabled: boolean;
  commissionType?: 'PERCENTAGE' | 'FIXED';
  commissionValue?: number;
}

export default function ProductNewEnhanced() {
  const navigate = useNavigate();
  const activeStoreId = useAuthStore((state) => state.activeStoreId);

  // Form state
  const [formData, setFormData] = useState<ProductFormData>({
    title: '',
    description: '',
    price: 0,
    metaTitle: '',
    metaDescription: '',
    metaKeywords: '',
    sku: '',
    status: ProductStatus.DRAFT,
    featured: false,
    tags: '',
    trackInventory: true,
    initialQuantity: 0,
    lowStockThreshold: 10,
    categoryId: '',
    affiliateEnabled: false,
    commissionType: 'PERCENTAGE',
    commissionValue: 10,
  });

  // Images
  const [images, setImages] = useState<{ file?: File; url: string; preview: string }[]>([]);

  // Variants - managed by VariantManager component
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [variants, setVariants] = useState<Variant[]>([]);
  const [hasVariants, setHasVariants] = useState(false);

  // Fetch categories
  const { data: categories } = useQuery({
    queryKey: ['categories', activeStoreId],
    queryFn: () => apiClient.get<any[]>('/products/categories'),
    enabled: !!activeStoreId,
  });

  // Auto-populate SKU when title changes
  useEffect(() => {
    if (formData.title && !formData.sku) {
      const prefix = formData.title.trim().substring(0, 3).toUpperCase().replace(/[^A-Z0-9]/g, 'X');
      const random = Math.floor(1000 + Math.random() * 9000);
      setFormData(prev => ({ ...prev, sku: `${prefix}-${random}` }));
    }
  }, [formData.title, formData.sku]);

  // Image handling
  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);

    files.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImages(prev => [...prev, {
          file,
          url: '',
          preview: reader.result as string,
        }]);
      };
      reader.readAsDataURL(file);
    });
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  // Create product mutation
  const createProduct = useMutation({
    mutationFn: async () => {
      // Helper for image compression
      const compressImage = async (file: File | Blob): Promise<Blob> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.src = URL.createObjectURL(file);
          img.onload = () => {
            const canvas = document.createElement('canvas');
            const MAX_WIDTH = 1200;
            const MAX_HEIGHT = 1200;
            let width = img.width;
            let height = img.height;

            if (width > height) {
              if (width > MAX_WIDTH) {
                height *= MAX_WIDTH / width;
                width = MAX_WIDTH;
              }
            } else {
              if (height > MAX_HEIGHT) {
                width *= MAX_HEIGHT / height;
                height = MAX_HEIGHT;
              }
            }
            canvas.width = width;
            canvas.height = height;
            const ctx = canvas.getContext('2d');
            ctx?.drawImage(img, 0, 0, width, height);
            canvas.toBlob((blob) => {
              resolve(blob || file);
            }, 'image/jpeg', 0.8);
          };
        });
      };

      // Helper for batching uploads (max 3 at a time)
      const uploadInBatches = async (items: any[], batchSize: number, uploadFn: (item: any) => Promise<any>) => {
        const results = [];
        for (let i = 0; i < items.length; i += batchSize) {
          const batch = items.slice(i, i + batchSize);
          const batchResults = await Promise.all(batch.map(uploadFn));
          results.push(...batchResults);
        }
        return results;
      };

      // Step 1: Upload main product images in batches of 3
      const uploadedImages = await uploadInBatches(images, 3, async (img) => {
        if (!img.file) return { url: img.url };

        const compressedBlob = await compressImage(img.file);
        const formDataUpload = new FormData();
        formDataUpload.append('file', compressedBlob, 'product.jpg');
        formDataUpload.append('folder', 'products');

        try {
          const uploadResult = await apiClient.post<any>('/uploads/image', formDataUpload, {
            headers: { 
              'Content-Type': 'multipart/form-data',
              'X-Store-Id': activeStoreId || ''
            },
          });
          return { url: uploadResult.url };
        } catch (error) {
          console.error('Upload failed', error);
          return { url: img.url }; // Fallback to local URL if upload fails
        }
      });

      // Step 2: Upload variant images in batches of 3
      const updatedVariants = await uploadInBatches(variants, 3, async (variant) => {
        let imageUrl = variant.image;
        if (variant.image && variant.image.startsWith('data:')) {
          const fetchResponse = await fetch(variant.image);
          const blob = await fetchResponse.blob();
          const compressedBlob = await compressImage(blob);

          const variantFormData = new FormData();
          variantFormData.append('file', compressedBlob, `variant-${variant.sku || 'v'}.jpg`);
          variantFormData.append('folder', 'variants');

          try {
            const uploadResult = await apiClient.post<any>('/uploads/image', variantFormData, {
              headers: { 
                'Content-Type': 'multipart/form-data',
                'X-Store-Id': activeStoreId || ''
              },
            });
            imageUrl = uploadResult.url;
          } catch (error) {
            console.error('Variant upload failed', error);
          }
        }
        return { ...variant, image: imageUrl };
      });

      // Calculate initialQuantity based on whether product has variants
      const calculatedInitialQuantity = hasVariants && updatedVariants.length > 0
        ? updatedVariants.reduce((sum, v) => sum + (v.quantity || 0), 0)
        : formData.initialQuantity;

      // Map variants to the format expected by the backend (mapping attributeValues from Record to Array)
      const formattedVariants = hasVariants && updatedVariants.length > 0
        ? updatedVariants.map((v: any) => ({
          title: v.title,
          sku: v.sku,
          price: v.price,
          compareAtPrice: v.compareAtPrice,
          quantity: v.quantity,
          available: v.available,
          image: v.image,
          attributeValues: Object.entries(v.attributeValues || {}).map(([attributeName, value]) => ({
            attributeName,
            value,
          })),
        }))
        : undefined;

      // Prepare product data
      const productData = {
        title: formData.title,
        description: formData.description,
        shortDescription: formData.shortDescription,
        price: formData.price,
        compareAtPrice: formData.compareAtPrice,
        costPerItem: formData.costPerItem,
        sku: formData.sku,
        categoryId: formData.categoryId,
        status: formData.status,
        featured: formData.featured,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean),
        trackInventory: formData.trackInventory,
        metaTitle: formData.metaTitle,
        metaDescription: formData.metaDescription,
        metaKeywords: formData.metaKeywords,
        images: uploadedImages,
        categories: formData.categoryId ? [formData.categoryId] : [],
        // Always send initialQuantity - either from form or calculated from variants
        initialQuantity: calculatedInitialQuantity,
        lowStockThreshold: formData.lowStockThreshold,
        attributes: hasVariants ? attributes : undefined,
        variants: formattedVariants,
        // Affiliate marketing
        affiliateEnabled: formData.affiliateEnabled,
        commissionType: formData.affiliateEnabled ? formData.commissionType : undefined,
        commissionValue: formData.affiliateEnabled ? formData.commissionValue : undefined,
      };

      return apiClient.post('/products', productData);
    },
    onSuccess: () => {
      useNotificationStore.getState().addNotification({
        type: 'success',
        title: 'Success',
        message: 'Product created successfully',
      });
      navigate(MERCHANT_ROUTES.PRODUCTS);
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    createProduct.mutate();
  };

  return (
    <div className="max-w-6xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate(MERCHANT_ROUTES.PRODUCTS)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">New Product</h1>
            <p className="text-muted-foreground mt-1">
              Create a new product with variants
            </p>
          </div>
        </div>
      </div>

      {/* Error Alert */}
      {createProduct.isError && (
        <Alert variant="destructive">
          {(createProduct.error as Error)?.message || 'Failed to create product'}
        </Alert>
      )}

      <form onSubmit={onSubmit} className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Information */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Basic Information</h2>

              <div>
                <Label htmlFor="title">Product Name *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Wireless Bluetooth Headphones"
                  required
                />
              </div>

              <div>
                <Label htmlFor="shortDescription">Short Description</Label>
                <Input
                  id="shortDescription"
                  value={formData.shortDescription || ''}
                  onChange={(e) => setFormData({ ...formData, shortDescription: e.target.value })}
                  placeholder="Brief product summary"
                />
              </div>

              <div>
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={6}
                  placeholder="Detailed product description"
                  required
                />
              </div>
            </Card>

            {/* Product Images */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Product Images</h2>

              <div className="grid grid-cols-3 gap-4">
                {images.map((img, index) => (
                  <div key={index} className="relative aspect-square">
                    <img
                      src={img.preview || img.url}
                      alt={`Preview ${index + 1}`}
                      className="w-full h-full object-cover rounded-lg border"
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2 h-8 w-8 p-0"
                      onClick={() => removeImage(index)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}

                <label className="aspect-square border-dashed border-muted-foreground/25 rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-muted/50 transition-colors">
                  <ImagePlus className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">Add Image</span>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                    className="hidden"
                  />
                </label>
              </div>
            </Card>

            {/* Pricing */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Pricing</h2>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label htmlFor="price">selling Price (KES) *</Label>
                  <Input
                    id="price"
                    type="number"
                    step="0.01"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="compareAtPrice">discounted price</Label>
                  <Input
                    id="compareAtPrice"
                    type="number"
                    step="0.01"
                    value={formData.compareAtPrice || ''}
                    onChange={(e) => setFormData({ ...formData, compareAtPrice: parseFloat(e.target.value) })}
                  />
                </div>

                <div>
                  <Label htmlFor="costPerItem">Buying price</Label>
                  <Input
                    id="costPerItem"
                    type="number"
                    step="0.01"
                    value={formData.costPerItem || ''}
                    onChange={(e) => setFormData({ ...formData, costPerItem: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
            </Card>

            {/* Inventory Management */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Inventory</h2>

              {/* Quantity fields - Only for non-variant products */}
              {!hasVariants && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="initialQuantity">Initial Quantity *</Label>
                      <Input
                        id="initialQuantity"
                        type="number"
                        min="0"
                        value={formData.initialQuantity || 0}
                        onChange={(e) => setFormData({ ...formData, initialQuantity: parseInt(e.target.value) || 0 })}
                        required={!hasVariants}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Starting stock quantity
                      </p>
                    </div>

                    <div>
                      <Label htmlFor="lowStockThreshold">Low Stock Alert</Label>
                      <Input
                        id="lowStockThreshold"
                        type="number"
                        min="0"
                        value={formData.lowStockThreshold || 10}
                        onChange={(e) => setFormData({ ...formData, lowStockThreshold: parseInt(e.target.value) || 10 })}
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        Alert when stock falls below this number
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Info for variant products */}
              {hasVariants && variants.length > 0 && (
                <Alert>
                  <p className="text-sm flex items-start gap-2">
                    <Package className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <span>This product has variants. Set the quantity for each variant in the table below.
                      The total inventory will be calculated automatically.</span>
                  </p>
                </Alert>
              )}
            </Card>

            {/* Product Variants */}
            <VariantManager
              hasVariants={hasVariants}
              onHasVariantsChange={setHasVariants}
              attributes={attributes}
              onAttributesChange={setAttributes}
              variants={variants}
              onVariantsChange={setVariants}
              defaultPrice={formData.price}
            />

            {/* SEO */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">SEO</h2>

              <div>
                <Label htmlFor="metaTitle">SEO Title</Label>
                <Input
                  id="metaTitle"
                  value={formData.metaTitle || ''}
                  onChange={(e) => setFormData({ ...formData, metaTitle: e.target.value })}
                  placeholder="Product name for search engines"
                />
              </div>

              <div>
                <Label htmlFor="metaDescription">SEO Description</Label>
                <Textarea
                  id="metaDescription"
                  value={formData.metaDescription || ''}
                  onChange={(e) => setFormData({ ...formData, metaDescription: e.target.value })}
                  rows={3}
                  placeholder="Description for search engines"
                />
              </div>

              <div>
                <Label htmlFor="metaKeywords">Keywords</Label>
                <Input
                  id="metaKeywords"
                  value={formData.metaKeywords || ''}
                  onChange={(e) => setFormData({ ...formData, metaKeywords: e.target.value })}
                  placeholder="keyword1, keyword2, keyword3"
                />
              </div>
            </Card>

            {/* Affiliate Marketing */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Affiliate Marketing</h2>

              {/* Enable Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div className="flex-1">
                  <Label htmlFor="affiliateEnabled" className="text-base font-medium cursor-pointer">
                    Enable Affiliate Marketing
                  </Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    Allow affiliates to promote this product and earn commissions
                  </p>
                </div>
                <Checkbox
                  id="affiliateEnabled"
                  checked={formData.affiliateEnabled}
                  onChange={(e) => setFormData({ ...formData, affiliateEnabled: e.target.checked })}
                />
              </div>

              {/* Commission Settings (shown when enabled) */}
              {formData.affiliateEnabled && (
                <div className="space-y-4 pt-4 border-t">
                  {/* Commission Type */}
                  <div>
                    <Label htmlFor="commissionType">Commission Type *</Label>
                    <Select
                      id="commissionType"
                      value={formData.commissionType}
                      onChange={(e) =>
                        setFormData({ ...formData, commissionType: e.target.value as 'PERCENTAGE' | 'FIXED' })
                      }
                      required={formData.affiliateEnabled}
                    >
                      <option value="PERCENTAGE">Percentage (%)</option>
                      <option value="FIXED">Fixed Amount (KES)</option>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1">
                      {formData.commissionType === 'PERCENTAGE'
                        ? 'Affiliates earn a percentage of each sale'
                        : 'Affiliates earn a fixed amount per sale'}
                    </p>
                  </div>

                  {/* Commission Value */}
                  <div>
                    <Label htmlFor="commissionValue">
                      Commission Value *
                      {formData.commissionType === 'PERCENTAGE' && ' (%)'}
                      {formData.commissionType === 'FIXED' && ' (KES)'}
                    </Label>
                    <Input
                      id="commissionValue"
                      type="number"
                      step={formData.commissionType === 'PERCENTAGE' ? '0.1' : '1'}
                      min="0"
                      max={formData.commissionType === 'PERCENTAGE' ? '100' : undefined}
                      value={formData.commissionValue || ''}
                      onChange={(e) =>
                        setFormData({ ...formData, commissionValue: parseFloat(e.target.value) || 0 })
                      }
                      placeholder={
                        formData.commissionType === 'PERCENTAGE'
                          ? 'e.g., 10 for 10%'
                          : 'e.g., 50 for KES 50'
                      }
                      required={formData.affiliateEnabled}
                    />
                    {formData.commissionType === 'PERCENTAGE' && formData.commissionValue && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Affiliates will earn {formData.commissionValue}% of each sale
                      </p>
                    )}
                    {formData.commissionType === 'FIXED' && formData.commissionValue && (
                      <p className="text-sm text-muted-foreground mt-1">
                        Affiliates will earn KES {formData.commissionValue.toFixed(2)} per sale
                      </p>
                    )}
                  </div>

                  {/* Commission Preview */}
                  {formData.price > 0 && formData.commissionValue && formData.commissionValue > 0 && (
                    <div className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-purple-950/20 dark:to-blue-950/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
                      <p className="text-sm font-semibold text-purple-900 dark:text-purple-100 mb-2 flex items-center gap-2">
                        <DollarSign className="w-4 h-4" />
                        Commission Preview
                      </p>
                      <div className="space-y-1">
                        <p className="text-xs text-purple-700 dark:text-purple-300">
                          selling Price: <span className="font-semibold">KES {formData.price.toFixed(2)}</span>
                        </p>
                        <p className="text-xs text-purple-700 dark:text-purple-300">
                          Affiliate Earns:{' '}
                          <span className="font-semibold text-purple-900 dark:text-purple-100">
                            KES{' '}
                            {formData.commissionType === 'PERCENTAGE'
                              ? ((formData.price * (formData.commissionValue || 0)) / 100).toFixed(2)
                              : (formData.commissionValue || 0).toFixed(2)}
                          </span>
                        </p>
                        <p className="text-xs text-purple-700 dark:text-purple-300">
                          You Receive:{' '}
                          <span className="font-semibold">
                            KES{' '}
                            {formData.commissionType === 'PERCENTAGE'
                              ? (formData.price - (formData.price * (formData.commissionValue || 0)) / 100).toFixed(2)
                              : (formData.price - (formData.commissionValue || 0)).toFixed(2)}
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Helpful Tips */}
                  <Alert>
                    <p className="text-sm">
                      <strong>💡 Tip:</strong> Typical commission rates range from 5-20%. Higher commissions attract more affiliates but reduce your profit margin.
                    </p>
                  </Alert>
                </div>
              )}
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Status</h2>

              <div>
                <Label htmlFor="status">Product Status</Label>
                <Select
                  id="status"
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as ProductStatus })}
                >
                  <option value={ProductStatus.ACTIVE}>Active</option>
                  <option value={ProductStatus.DRAFT}>Draft</option>
                  <option value={ProductStatus.ARCHIVED}>Archived</option>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Checkbox
                  id="featured"
                  checked={formData.featured}
                  onChange={(e) => setFormData({ ...formData, featured: e.target.checked })}
                />
                <Label htmlFor="featured" className="cursor-pointer">
                  Featured product
                </Label>
              </div>
            </Card>

            {/* Organization */}
            <Card className="p-6 space-y-4">
              <h2 className="text-xl font-semibold">Organization</h2>

              <div>
                <Label htmlFor="categoryId">Category</Label>
                <Select
                  id="categoryId"
                  value={formData.categoryId || ''}
                  onChange={(e) => setFormData({ ...formData, categoryId: e.target.value })}
                >
                  <option value="">No category</option>
                  {categories?.map((category: any) => (
                    <option key={category.id} value={category.id}>
                      {category.name}
                    </option>
                  ))}
                </Select>
              </div>

              <div>
                <Label htmlFor="sku">SKU *</Label>
                <div className="flex gap-2">
                  <Input
                    id="sku"
                    value={formData.sku}
                    onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
                    placeholder="e.g., WHB-001"
                    required
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      const prefix = formData.title ? formData.title.substring(0, 3).toUpperCase() : 'SKU';
                      const random = Math.floor(1000 + Math.random() * 9000);
                      const generatedSku = `${prefix}-${random}`;
                      setFormData({ ...formData, sku: generatedSku });
                    }}
                  >
                    Generate
                  </Button>
                </div>
              </div>

              <div>
                <Label htmlFor="tags">Tags</Label>
                <Input
                  id="tags"
                  value={formData.tags}
                  onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                  placeholder="wireless, bluetooth, audio"
                />
                <p className="text-xs text-muted-foreground mt-1">
                  Separate tags with commas
                </p>
              </div>
            </Card>

            {/* Actions */}
            <Card className="p-6 space-y-4">
              <Button
                type="submit"
                className="w-full"
                disabled={createProduct.isPending}
              >
                {createProduct.isPending ? (
                  'Creating...'
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Create Product
                  </>
                )}
              </Button>

              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => navigate(MERCHANT_ROUTES.PRODUCTS)}
              >
                Cancel
              </Button>
            </Card>
          </div>
        </div>
      </form>
    </div>
  );
}

